import {makeAutoObservable, observable} from "mobx";
import {inject, injectable} from "inversify";
import {useNavigate} from "react-router-dom";
import {useLocation} from "react-router";
import {ViewController} from "data/types/structure";
import {Bindings} from "data/constants/bindings";
import {TutorialType} from "data/enums";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {ITutorialStore} from "data/stores/tutorial/tutorial.store";
import type {IWelcomeModalStore} from "data/stores/welcome_modal/welcome_modal.store";
import {HP_PATH, HP_TUTORIAL, LP_PATH, LP_TUTORIAL} from "data/constants";

interface IProps {
	navigate: ReturnType<typeof useNavigate>;
	location: ReturnType<typeof useLocation>;
}

export interface IModalWelcomeLightPredictorController extends ViewController<IProps> {
	i18n: ILocalizationStore;
	get isOpen(): boolean;

	closeHandler: () => void;
	redirectHandler: () => void;
}

@injectable()
export class ModalWelcomeLightPredictorController implements IModalWelcomeLightPredictorController {
	@observable private _navigate!: IProps["navigate"];
	@observable private _location!: IProps["location"];

	get isOpen(): boolean {
		return this._welcomeModalStore.isOpen;
	}

	private get isLPRoute() {
		return window.location.pathname === LP_PATH;
	}

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.WelcomeModalStore) private _welcomeModalStore: IWelcomeModalStore,
		@inject(Bindings.TutorialStore) private _tutorialStore: ITutorialStore
	) {
		makeAutoObservable(this);
	}

	init(param: IProps) {
		this._navigate = param.navigate;
		this._location = param.location;
	}

	closeHandler = () => {
		const isUserViewedWelcomeModal = JSON.parse(
			localStorage.getItem(LP_TUTORIAL) || "false"
		) as boolean;

		this._welcomeModalStore.hideModal();

		if (this.isLPRoute && !isUserViewedWelcomeModal) {
			this.openLpTutorial();
		}
	};

	redirectHandler = () => {
		this._welcomeModalStore.hideModal();

		if (this.isLPRoute) {
			this._navigate(HP_PATH, {replace: true});
			this.openHpTutorial();
		} else {
			this._navigate(LP_PATH, {replace: true});
			this.openLpTutorial();
		}
	};

	private openLpTutorial() {
		const isUserViewedWelcomeModal = JSON.parse(
			localStorage.getItem(LP_TUTORIAL) || "false"
		) as boolean;

		if (isUserViewedWelcomeModal) return;

		localStorage.setItem(LP_TUTORIAL, JSON.stringify(true));
		this._tutorialStore.openTutorial(TutorialType.LIGHT_PREDICTOR);
	}

	private openHpTutorial() {
		const isUserViewedWelcomeModal = JSON.parse(
			localStorage.getItem(HP_TUTORIAL) || "false"
		) as boolean;

		if (isUserViewedWelcomeModal) return;

		localStorage.setItem(HP_TUTORIAL, JSON.stringify(true));
		this._tutorialStore.openTutorial(TutorialType.HEAVE_PREDICTOR);
	}
}
